import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  SvgIcon,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TableChartIcon from '@mui/icons-material/TableChart';
import FolderIcon from '@mui/icons-material/Folder';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import JsonColumnExamplesDialog from './JsonColumnExamplesDialog';

const EXTERNAL_DATA_SCHEMA = 'external_data';

function ExcelIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.5L14.5 2zM14 3.5 19.5 9H14V3.5zM8.2 11h2.05l1.15 2.45L12.6 11h2.05l-2.2 4 2.3 4h-2.1l-1.25-2.55L11 19H8.9l2.25-4L8.2 11z"
      />
    </SvgIcon>
  );
}


function isExternalDataTable(table) {
  const schema = table?.schema || String(table?.id || '').split('.')[0];
  return schema === EXTERNAL_DATA_SCHEMA;
}

function groupBySchema(tables) {
  const groups = new Map();
  for (const t of tables || []) {
    if (t.scanStatus === 'removed') continue;
    const schema = t.schema || String(t.id || '').split('.')[0] || 'dbo';
    if (!groups.has(schema)) groups.set(schema, []);
    groups.get(schema).push(t);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([schema, items]) => ({
      schema,
      tables: items.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id)),
    }));
}

function isNativeJson(dataType) {
  const t = String(dataType || '').toLowerCase();
  return t === 'json' || t === 'jsonb' || t.includes('json');
}

/** Árbol esquema → tablas con checkbox (modelo tipo Power BI). */
export function ModelTableTree({
  tables = [],
  onToggleTable,
  onToggleTables,
  onUpdateColumn,
  onDeleteExternalTable,
  deleteBusy = false,
  onExportExcel,
  onImportExcel,
  excelDisabled = false,
  excelDisabledReason = '',
  getAuthHeaders,
  compact = false,
}) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [expandedTableId, setExpandedTableId] = useState(null);
  const [jsonEdit, setJsonEdit] = useState(null); // { tableId, column }
  const [pendingDelete, setPendingDelete] = useState(null);
  const [excelMenuAnchor, setExcelMenuAnchor] = useState(null);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = (tables || []).filter((t) => {
      if (t.scanStatus === 'removed') return false;
      if (!q) return true;
      return (
        String(t.id || '').toLowerCase().includes(q) ||
        (t.name || '').toLowerCase().includes(q) ||
        (t.schema || '').toLowerCase().includes(q)
      );
    });
    return groupBySchema(filtered);
  }, [tables, search]);

  const inModelCount = useMemo(
    () => (tables || []).filter((t) => t.includedInAI && t.scanStatus !== 'ignored').length,
    [tables]
  );

  const toggleSchema = (schema, schemaTables, checked) => {
    const ids = schemaTables
      .filter((t) => Boolean(t.includedInAI) !== checked)
      .map((t) => t.id);
    if (!ids.length) return;
    // Una sola actualizacion de estado para todo el esquema (ver comentario en
    // handleToggleTables de DataModelAdmin.js) -- llamar a onToggleTable una
    // vez por tabla en un loop pierde todas menos la ultima llamada.
    if (onToggleTables) {
      onToggleTables(ids, checked);
    } else {
      ids.forEach((id) => onToggleTable?.(id, checked));
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        minHeight: 0,
        maxHeight: compact ? '100%' : 520,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ pt: 0.35 }}>
            Tablas del modelo
          </Typography>
          {(onExportExcel || onImportExcel) && (
            <>
              <Tooltip title={excelDisabled ? excelDisabledReason : ''} disableHoverListener={!excelDisabled}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ExcelIcon sx={{ color: excelDisabled ? 'inherit' : '#217346' }} />}
                    onClick={(e) => setExcelMenuAnchor(e.currentTarget)}
                    disabled={excelDisabled}
                    sx={{
                      textTransform: 'none',
                      flexShrink: 0,
                      fontSize: 11,
                      lineHeight: 1.15,
                      py: 0.4,
                      maxWidth: 148,
                      whiteSpace: 'normal',
                      textAlign: 'left',
                    }}
                  >
                    Exportar/importar
                  </Button>
                </span>
              </Tooltip>
              <Menu
                anchorEl={excelMenuAnchor}
                open={Boolean(excelMenuAnchor)}
                onClose={() => setExcelMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    setExcelMenuAnchor(null);
                    onExportExcel?.();
                  }}
                  disabled={excelDisabled}
                >
                  <ListItemIcon>
                    <FileDownloadIcon fontSize="small" />
                  </ListItemIcon>
                  Exportar
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setExcelMenuAnchor(null);
                    onImportExcel?.();
                  }}
                  disabled={excelDisabled}
                >
                  <ListItemIcon>
                    <FileUploadIcon fontSize="small" />
                  </ListItemIcon>
                  Importar
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {inModelCount} seleccionada{inModelCount !== 1 ? 's' : ''}. Expande una tabla → columnas
          JSON → <strong>Ejemplos</strong> para pegar JSON crudo e inferir rutas.
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar tabla…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', py: 0.5 }}>
        {groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            Sin tablas. Usa &quot;Actualizar esquema&quot; para cargar la BD.
          </Typography>
        ) : (
          groups.map(({ schema, tables: schemaTables }) => {
            const open = expanded[schema] !== false;
            const selectedInSchema = schemaTables.filter((t) => t.includedInAI).length;
            const allSelected = selectedInSchema === schemaTables.length && schemaTables.length > 0;
            const someSelected = selectedInSchema > 0 && !allSelected;
            return (
              <Box key={schema}>
                <ListItem disablePadding dense>
                  <ListItemButton onClick={() => setExpanded((p) => ({ ...p, [schema]: !open }))} sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <FolderIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={schema}
                      secondary={`${selectedInSchema}/${schemaTables.length}`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600, noWrap: true }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                      sx={{ minWidth: 0 }}
                    />
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggleSchema(schema, schemaTables, e.target.checked)}
                      sx={{ flexShrink: 0 }}
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={open} unmountOnExit>
                  <List dense disablePadding sx={{ pl: 2 }}>
                    {schemaTables.map((t) => {
                      const tableOpen = expandedTableId === t.id;
                      return (
                        <Box key={t.id}>
                          <ListItem disablePadding>
                            <ListItemButton
                              onClick={() => setExpandedTableId(tableOpen ? null : t.id)}
                              sx={{ py: 0.15 }}
                            >
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                {tableOpen ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </ListItemIcon>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Checkbox
                                  edge="start"
                                  size="small"
                                  checked={Boolean(t.includedInAI)}
                                  tabIndex={-1}
                                  disableRipple
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => onToggleTable?.(t.id, e.target.checked)}
                                />
                              </ListItemIcon>
                              <ListItemIcon sx={{ minWidth: 24 }}>
                                <TableChartIcon
                                  sx={{ fontSize: 16 }}
                                  color={isExternalDataTable(t) ? 'success' : t.includedInAI ? 'primary' : 'disabled'}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={t.name || t.id}
                                secondary={t.id}
                                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                                sx={{ minWidth: 0 }}
                              />
                              {t.scanStatus === 'new' && (
                                <Chip
                                  label="Nueva"
                                  size="small"
                                  color="info"
                                  sx={{ ml: 0.5, flexShrink: 0 }}
                                />
                              )}
                              {isExternalDataTable(t) && onDeleteExternalTable && (
                                <Tooltip title="Eliminar tabla importada">
                                  <IconButton
                                    size="small"
                                    aria-label={`Eliminar ${t.name || t.id}`}
                                    disabled={deleteBusy}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPendingDelete(t);
                                    }}
                                    sx={{ ml: 0.25, flexShrink: 0 }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </ListItemButton>
                          </ListItem>
                          <Collapse in={tableOpen} unmountOnExit>
                            <Box sx={{ pl: 4, pr: 1, pb: 1 }}>
                              {(t.columns || []).length === 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                  Sin columnas
                                </Typography>
                              ) : (
                                (t.columns || []).map((col) => {
                                  const nativeJson = isNativeJson(col.dataType);
                                  const contentType = col.contentType || '';
                                  const isJson = nativeJson || contentType === 'json';
                                  const exCount = Array.isArray(col.jsonExamples)
                                    ? col.jsonExamples.length
                                    : 0;
                                  const pathCount = Array.isArray(col.jsonPaths)
                                    ? col.jsonPaths.length
                                    : 0;
                                  return (
                                    <Box
                                      key={col.name}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        py: 0.35,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        flexWrap: 'wrap',
                                      }}
                                    >
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" noWrap display="block">
                                          {col.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                          {col.dataType || '?'}
                                          {nativeJson ? ' · JSON nativo' : ''}
                                          {exCount > 0 ? ` · ${exCount} ej.` : ''}
                                          {pathCount > 0 ? ` · ${pathCount} rutas` : ''}
                                        </Typography>
                                      </Box>
                                      {!nativeJson && onUpdateColumn && (
                                        <FormControl size="small" sx={{ minWidth: 88 }}>
                                          <InputLabel shrink>Contenido</InputLabel>
                                          <Select
                                            label="Contenido"
                                            displayEmpty
                                            value={contentType}
                                            onChange={(e) =>
                                              onUpdateColumn(t.id, col.name, {
                                                contentType: e.target.value || null,
                                              })
                                            }
                                            sx={{ fontSize: 11, height: 28 }}
                                          >
                                            <MenuItem value="">
                                              <em>Auto</em>
                                            </MenuItem>
                                            <MenuItem value="string">Texto</MenuItem>
                                            <MenuItem value="json">JSON</MenuItem>
                                          </Select>
                                        </FormControl>
                                      )}
                                      {isJson && (
                                        <Chip label="JSON" size="small" color="secondary" sx={{ height: 20 }} />
                                      )}
                                      {(isJson || contentType === '' || nativeJson) && onUpdateColumn && (
                                        <Button
                                          size="small"
                                          startIcon={<DataObjectIcon sx={{ fontSize: 14 }} />}
                                          onClick={() =>
                                            setJsonEdit({
                                              tableId: t.id,
                                              column: col.name,
                                              jsonExamples: col.jsonExamples || [],
                                              jsonPaths: col.jsonPaths || [],
                                            })
                                          }
                                          sx={{ fontSize: 10, minWidth: 0, px: 0.75, height: 26 }}
                                        >
                                          Ejemplos
                                        </Button>
                                      )}
                                    </Box>
                                  );
                                })
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={deleteBusy ? undefined : () => setPendingDelete(null)}
      >
        <DialogTitle>Eliminar tabla importada</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se va a borrar <strong>{pendingDelete?.id}</strong> de Postgres (schema external_data) y
            del modelo, incluyendo sus relaciones. Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={deleteBusy}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteBusy}
            onClick={async () => {
              if (!pendingDelete) return;
              try {
                const ok = await onDeleteExternalTable?.(pendingDelete);
                if (ok !== false) setPendingDelete(null);
              } catch {
                /* el padre muestra el error */
              }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <JsonColumnExamplesDialog
        open={Boolean(jsonEdit)}
        onClose={() => setJsonEdit(null)}
        tableId={jsonEdit?.tableId}
        column={jsonEdit?.column}
        initialExamples={jsonEdit?.jsonExamples}
        initialPaths={jsonEdit?.jsonPaths}
        getAuthHeaders={getAuthHeaders}
        onSave={(patch) => {
          if (!jsonEdit) return;
          onUpdateColumn?.(jsonEdit.tableId, jsonEdit.column, patch);
        }}
      />
    </Paper>
  );
}

export default ModelTableTree;
